import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import API_URL from "../../config/ApiUrl";

// ----- Thunks -------------------------------------------------

export const lookupReceivablePRs = createAsyncThunk(
  "receiveSession/lookupReceivablePRs",
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(
        API_URL.RECEIVE_SESSION.LOOKUP_PRS,
        payload,
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const startReceiveSession = createAsyncThunk(
  "receiveSession/startReceiveSession",
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(
        API_URL.RECEIVE_SESSION.START,
        payload,
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const getReceiveSessions = createAsyncThunk(
  "receiveSession/getReceiveSessions",
  async (params = {}, { rejectWithValue }) => {
    try {
      const {
        status = 1, // Active
        search = "",
        pageNumber = 1,
        pageSize = 10,
        orderColumn = "LastActivityAt",
        orderBy = "desc",
      } = params;
      const query = new URLSearchParams({
        status: String(status),
        PageNumber: String(pageNumber),
        PageSize: String(pageSize),
        OrderColumn: orderColumn,
        OrderBy: orderBy,
      });
      if (search) query.append("search", search);
      const { data } = await axios.get(
        `${API_URL.RECEIVE_SESSION.LIST}?${query.toString()}`,
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const joinReceiveSession = createAsyncThunk(
  "receiveSession/joinReceiveSession",
  async (sessionId, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(API_URL.RECEIVE_SESSION.JOIN(sessionId));
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const getReceiveSessionDetail = createAsyncThunk(
  "receiveSession/getReceiveSessionDetail",
  async (sessionId, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(API_URL.RECEIVE_SESSION.DETAIL(sessionId));
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const getReceiveSessionProgress = createAsyncThunk(
  "receiveSession/getReceiveSessionProgress",
  async (sessionId, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(API_URL.RECEIVE_SESSION.PROGRESS(sessionId));
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const addPRsToReceiveSession = createAsyncThunk(
  "receiveSession/addPRsToReceiveSession",
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(
        API_URL.RECEIVE_SESSION.ADD_PRS,
        payload,
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const removePRFromReceiveSession = createAsyncThunk(
  "receiveSession/removePRFromReceiveSession",
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(
        API_URL.RECEIVE_SESSION.REMOVE_PR,
        payload,
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const scanSessionBoxes = createAsyncThunk(
  "receiveSession/scanSessionBoxes",
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(API_URL.RECEIVE_SESSION.SCAN, payload);
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const finalizeMarkMissing = createAsyncThunk(
  "receiveSession/finalizeMarkMissing",
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(
        API_URL.RECEIVE_SESSION.FINALIZE_MARK_MISSING,
        payload,
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const closeReceiveSession = createAsyncThunk(
  "receiveSession/closeReceiveSession",
  async (sessionId, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(API_URL.RECEIVE_SESSION.CLOSE(sessionId));
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const resetReceiveSession = createAsyncThunk(
  "receiveSession/resetReceiveSession",
  async (sessionId, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(API_URL.RECEIVE_SESSION.RESET(sessionId));
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const cancelReceiveSession = createAsyncThunk(
  "receiveSession/cancelReceiveSession",
  async (sessionId, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(API_URL.RECEIVE_SESSION.CANCEL(sessionId));
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

// ----- Slice --------------------------------------------------

const initialState = {
  active: {
    loading: false,
    sessions: [],
    total: 0,
    pageNumber: 1,
    pageSize: 10,
  },
  detail: { loading: false, data: null, version: 0 },
  progress: { loading: false, data: null },
  // Screen 1 working set — PRs the user has added but not yet created a session for.
  // Mirrored to localStorage so accidental reload doesn't lose the list.
  selectionDraft: { prs: [] },
  scanStatus: "idle",
};

const LOCAL_STORAGE_KEY = "receiveSession.selectionDraft.prs";

function loadDraftFromStorage() {
  try {
    const raw = localStorage.getItem(LOCAL_STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveDraftToStorage(prs) {
  try {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(prs));
  } catch {
    /* ignore storage failures */
  }
}

const receiveSessionSlice = createSlice({
  name: "receiveSession",
  initialState: {
    ...initialState,
    selectionDraft: { prs: loadDraftFromStorage() },
  },
  reducers: {
    addToSelectionDraft(state, action) {
      const existing = new Set(state.selectionDraft.prs.map((p) => p.purchaseReceiveId));
      const toAdd = (action.payload || []).filter(
        (p) => !existing.has(p.purchaseReceiveId),
      );
      state.selectionDraft.prs.push(...toAdd);
      saveDraftToStorage(state.selectionDraft.prs);
    },
    removeFromSelectionDraft(state, action) {
      state.selectionDraft.prs = state.selectionDraft.prs.filter(
        (p) => p.purchaseReceiveId !== action.payload,
      );
      saveDraftToStorage(state.selectionDraft.prs);
    },
    clearSelectionDraft(state) {
      state.selectionDraft.prs = [];
      saveDraftToStorage([]);
    },
    setScanStatus(state, action) {
      state.scanStatus = action.payload;
    },
    // Patch local detail after a scan — mark boxes as scanned, update counters
    // from the scan-response's Progress payload. Avoids a full detail refetch.
    applyScanResult(state, action) {
      const { appliedBoxIds, userId, scannedAt, progress } = action.payload || {};
      const d = state.detail.data;
      if (!d) return;
      if (Array.isArray(appliedBoxIds) && appliedBoxIds.length > 0 && Array.isArray(d.boxes)) {
        const applied = new Set(appliedBoxIds);
        for (const box of d.boxes) {
          if (applied.has(box.purchaseReceiveBoxId)) {
            box.isBoxScanned = true;
            if (userId) box.boxScannedBy = userId;
            if (scannedAt) box.boxScannedDate = scannedAt;
          }
        }
      }
      if (progress) {
        d.totalBoxes = progress.totalBoxes ?? d.totalBoxes;
        d.scannedBoxes = progress.scannedBoxes ?? d.scannedBoxes;
        d.missingBoxes = progress.missingBoxes ?? d.missingBoxes;
        d.lastActivityAt = progress.lastActivityAt ?? d.lastActivityAt;
        if (Array.isArray(progress.prs) && Array.isArray(d.prs)) {
          const byId = new Map(progress.prs.map((p) => [p.purchaseReceiveId, p]));
          for (const pr of d.prs) {
            const p = byId.get(pr.purchaseReceiveId);
            if (p) {
              pr.totalBoxes = p.totalBoxes;
              pr.scannedBoxes = p.scannedBoxes;
              pr.missingBoxes = p.missingBoxes;
              if (p.isAutoCompleted && !pr.autoCompletedAt) {
                pr.autoCompletedAt = progress.lastActivityAt ?? new Date().toISOString();
              }
            }
          }
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getReceiveSessions.pending, (state) => {
        state.active.loading = true;
      })
      .addCase(getReceiveSessions.fulfilled, (state, action) => {
        state.active.loading = false;
        state.active.sessions = action.payload?.list || [];
        state.active.total = action.payload?.totalRecords || 0;
        state.active.pageNumber = action.payload?.pageNumber || 1;
        state.active.pageSize = action.payload?.pageSize || 10;
      })
      .addCase(getReceiveSessions.rejected, (state) => {
        state.active.loading = false;
      });

    const setDetail = (state, action) => {
      state.detail.loading = false;
      state.detail.data = action.payload;
      state.detail.version = action.payload?.sessionVersion ?? 0;
    };
    builder
      .addCase(getReceiveSessionDetail.pending, (state) => {
        state.detail.loading = true;
      })
      .addCase(getReceiveSessionDetail.fulfilled, setDetail)
      .addCase(getReceiveSessionDetail.rejected, (state) => {
        state.detail.loading = false;
      })
      .addCase(startReceiveSession.fulfilled, setDetail)
      .addCase(joinReceiveSession.fulfilled, setDetail)
      .addCase(addPRsToReceiveSession.fulfilled, setDetail)
      .addCase(removePRFromReceiveSession.fulfilled, setDetail);

    builder
      .addCase(getReceiveSessionProgress.pending, (state) => {
        state.progress.loading = true;
      })
      .addCase(getReceiveSessionProgress.fulfilled, (state, action) => {
        state.progress.loading = false;
        state.progress.data = action.payload;
      })
      .addCase(getReceiveSessionProgress.rejected, (state) => {
        state.progress.loading = false;
      });
  },
});

export const {
  addToSelectionDraft,
  removeFromSelectionDraft,
  clearSelectionDraft,
  setScanStatus,
  applyScanResult,
} = receiveSessionSlice.actions;

export default receiveSessionSlice.reducer;
